<template>
  <div class="min-h-screen relative overflow-hidden">
    <!-- Animierter Background -->
    <div class="fixed inset-0 -z-10">
      <!-- Gradient Background -->
      <div class="absolute inset-0 bg-gradient-to-br from-gray-50 via-red-50/20 to-gray-100 dark:from-gray-900 dark:via-red-950/10 dark:to-gray-800"></div>
      
      <!-- Animierte Gradient Blobs -->
      <div class="absolute top-0 -left-4 w-96 h-96 bg-red-400/10 dark:bg-red-500/5 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-70 animate-blob"></div>
      <div class="absolute top-0 -right-4 w-96 h-96 bg-gray-400/10 dark:bg-gray-500/5 rounded-full mix-blend-multiply dark:mix-blend-soft-light filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
      
      <!-- Grid Pattern Overlay -->
      <div class="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] dark:bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)]"></div>
    </div>

    <Navigation />

    <!-- Hero Section -->
    <section class="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
      <div class="max-w-7xl mx-auto">
        <div class="flex flex-col lg:flex-row items-center gap-12 mb-12">
          <!-- Text Content -->
          <div class="flex-1 text-center lg:text-left">
            <div v-if="logoUrl" class="flex justify-center lg:justify-start mb-8 reveal">
              <img 
                :key="`m-drei-logo-${isDark}-${logoUrl}`"
                :src="logoUrl"
                alt="M-Drei Logo" 
                class="h-20 md:h-24 object-contain transition-opacity duration-300"
              />
            </div>
            <h1 class="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 reveal delay-100">
              M-Drei Möbelfertigteile
            </h1>
            <div class="mt-4 h-1.5 w-24 bg-[#a50e34] mx-auto lg:mx-0 rounded-full reveal delay-200"></div>
            <p class="mt-6 text-xl md:text-2xl font-medium text-gray-900 dark:text-white reveal delay-300">
              WordPress E-Commerce & Plugin-Entwicklung
            </p>
            <div class="mt-8 text-lg text-gray-900 dark:text-white leading-relaxed reveal delay-400">
              <p class="mb-4">
                <span class="font-bold">M-DREI</span> entwickelt und vertreibt <span class="font-bold">maßgefertigte Möbelfertigteile</span> sowie eine eigene <span class="font-bold">3D-Planungssoftware</span> für Schreiner und Tischler. Ziel ist es, Planung, Produktion und Schulung digital und effizient zu verbinden.
              </p>
              <p class="mb-4">
                Die <span class="font-bold">WordPress-Website</span> wurde auf Basis eines vorgegebenen Designs technisch umgesetzt. Mein Schwerpunkt lag auf der <span class="font-bold">Frontend-Implementierung</span> mit <span class="font-bold">Elementor</span> sowie der Entwicklung komplexer individueller <span class="font-bold">Backend-Lösungen</span>. Dazu zählen mehrere <span class="font-bold">Custom-Plugins</span> für Video-Suche, Event- und Lizenzverwaltung sowie <span class="font-bold">API-Integrationen</span> mit Vimeo, Zoom und RapidMail.
              </p>
              <p class="text-base font-semibold mb-8">
                Umgesetzt in enger Zusammenarbeit mit Designer <a href="https://hb-portfolio.de" target="_blank" class="text-[#a50e34] dark:text-red-400 hover:underline transition-colors">Benjamin Heiling</a>.
              </p>
            </div>
          </div>

          <!-- Hero Screenshot -->
          <div class="flex-1 w-full reveal delay-300">
            <div class="relative group">
              <div class="absolute -inset-4 bg-gradient-to-r from-[#a50e34]/20 to-red-600/20 rounded-[2rem] blur-2xl opacity-0 group-hover:opacity-100 transition duration-500"></div>
              <div class="relative bg-white dark:bg-gray-800 rounded-2xl overflow-hidden shadow-2xl border border-gray-200 dark:border-gray-700 cursor-pointer" @click="openLightbox('/projects/m-drei/WebsiteHero.webp')">
                <img 
                  src="/projects/m-drei/WebsiteHero.webp" 
                  alt="M-Drei Webseite Screenshot" 
                  class="w-full h-auto object-cover hover:scale-105 transition duration-700"
                />
                <div class="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <div class="bg-white/20 backdrop-blur-md p-3 rounded-full">
                    <MagnifyingGlassPlusIcon class="w-6 h-6 text-white" />
                  </div>
                </div>
              </div>
            </div>
            <!-- CTA Button unter dem Hero Image -->
            <div class="flex justify-center mt-6 reveal delay-500">
              <a 
                href="https://www.m-drei.de" 
                target="_blank" 
                rel="noopener noreferrer"
                class="inline-flex items-center space-x-3 px-8 py-4 bg-[#a50e34] text-white font-bold rounded-2xl shadow-xl shadow-red-900/20 hover:bg-red-700 transition-all duration-300 hover:scale-105 group"
              >
                <GlobeAltIcon class="w-6 h-6 group-hover:rotate-12 transition-transform" />
                <span>Zur Webseite</span>
              </a>
            </div>
          </div>
        </div>

        <!-- Tech Stack -->
        <div class="mb-12 reveal delay-500">
          <div class="flex flex-wrap justify-center gap-2 md:gap-3">
            <span
              v-for="tech in displayedTechnologies"
              :key="tech"
              class="px-2 py-1 md:px-4 md:py-2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-md md:rounded-lg text-xs md:text-sm font-semibold text-gray-800 dark:text-gray-200 shadow-sm md:shadow-md border border-gray-300/70 dark:border-gray-600/60 md:border-2 hover:border-[#a50e34] dark:hover:border-red-400 transition-colors duration-300"
            >
              {{ tech }}
            </span>
          </div>
          <!-- "Mehr anzeigen" Button nur auf mobil -->
          <button
            v-if="!showAllTechs && project.technologies.length > 8"
            @click="showAllTechs = true"
            class="mt-3 mx-auto block md:hidden text-sm font-semibold text-[#a50e34] dark:text-red-400 hover:underline transition-colors"
          >
            + {{ project.technologies.length - 8 }} weitere anzeigen
          </button>
          <button
            v-else-if="showAllTechs"
            @click="showAllTechs = false"
            class="mt-3 mx-auto block md:hidden text-sm font-semibold text-[#a50e34] dark:text-red-400 hover:underline transition-colors"
          >
            Weniger anzeigen
          </button>
        </div>
      </div>
    </section>

    <!-- Project Stats / Highlights Grid -->
    <section class="pb-20 px-4 sm:px-6 lg:px-8">
      <div class="max-w-7xl mx-auto">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
          <div v-for="(stat, i) in stats" :key="i" class="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md p-4 md:p-6 rounded-xl md:rounded-2xl border-2 border-gray-300/60 dark:border-gray-600/50 shadow-lg text-center reveal" :style="{ transitionDelay: `${i * 100}ms` }">
            <component :is="stat.icon" class="w-6 h-6 md:w-8 md:h-8 mx-auto mb-2 md:mb-4 text-[#a50e34] dark:text-red-400" />
            <div class="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-1">{{ stat.value }}</div>
            <div class="text-xs md:text-sm text-gray-700 dark:text-gray-300 uppercase tracking-wider font-semibold leading-tight">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="py-20 px-4 sm:px-6 lg:px-8 bg-white/30 dark:bg-black/10 backdrop-blur-sm">
      <div class="max-w-7xl mx-auto">
        <div class="text-center mb-16 reveal">
          <h2 class="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Highlights & Lösungen
          </h2>
          <div class="mt-4 h-1.5 w-24 bg-[#a50e34] mx-auto rounded-full"></div>
          <p class="mt-6 text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Innovative Lösungen für komplexe Anforderungen
          </p>
        </div>

        <div class="space-y-16 md:space-y-24">
          <div
            v-for="(feature, index) in sortedFeatures"
            :key="feature.id"
            class="feature-item reveal"
            :style="{ transitionDelay: `${index * 100}ms` }"
          >
            <!-- Modern Card Layout: Mobile = Single Container, Desktop = Grid -->
            <div 
              class="relative group flex flex-col md:grid md:grid-cols-2 md:gap-8"
            >
              <!-- Mobile: Gemeinsamer Container für Text und Media -->
              <div class="md:hidden relative bg-gradient-to-br from-white via-white to-gray-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 backdrop-blur-md rounded-3xl shadow-2xl border-2 border-gray-200/60 dark:border-gray-700/50 group-hover:border-[#a50e34]/60 dark:group-hover:border-red-500/60 transition-all duration-500 overflow-hidden">
                <!-- Decorative Background Element -->
                <div class="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#a50e34]/5 to-transparent rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                
                <!-- Text Content (oben auf mobil) -->
                <div class="relative p-6 pb-4">
                  <!-- Icon Badge -->
                  <div class="relative flex items-start justify-between mb-4">
                    <div class="flex items-center gap-3">
                      <div class="p-2.5 rounded-2xl bg-gradient-to-br from-[#a50e34]/10 to-red-600/10 dark:from-[#a50e34]/20 dark:to-red-600/20 border border-[#a50e34]/20 dark:border-red-500/20 group-hover:scale-110 transition-transform duration-300">
                        <component :is="feature.icon" class="w-6 h-6 text-[#a50e34] dark:text-red-400" />
                      </div>
                      <div class="px-3 py-1 rounded-full bg-gradient-to-r from-red-100 to-red-50 dark:from-red-900/40 dark:to-red-800/30 text-[#a50e34] dark:text-red-400 text-xs font-bold border border-[#a50e34]/20 dark:border-red-500/20">
                        #{{ String(index + 1).padStart(2, '0') }}
                      </div>
                    </div>
                  </div>

                  <!-- Title -->
                  <h3 class="relative text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4 group-hover:text-[#a50e34] dark:group-hover:text-red-400 transition-colors duration-300 leading-tight">
                    {{ feature.title }}
                  </h3>

                  <!-- Description -->
                  <p class="relative text-base text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                    {{ feature.description }}
                  </p>

                  <!-- Tags -->
                  <div v-if="feature.tags" class="relative flex flex-wrap gap-2">
                    <span 
                      v-for="tag in feature.tags" 
                      :key="tag" 
                      class="text-xs font-semibold px-3 py-1 max-w-[45%] bg-gray-100/80 dark:bg-gray-700/60 rounded-full text-gray-700 dark:text-gray-300 border border-gray-200/50 dark:border-gray-600/50 group-hover:border-[#a50e34]/30 dark:group-hover:border-red-500/30 transition-colors duration-300"
                    >
                      {{ tag }}
                    </span>
                  </div>
                </div>

                <!-- Media Container (unten auf mobil) -->
                <div class="relative w-full group/media px-4 pb-4">
                  <!-- Animated Background Glow -->
                  <div class="absolute -inset-4 bg-gradient-to-r from-[#a50e34]/20 via-red-600/20 to-[#a50e34]/20 rounded-[2rem] blur-2xl opacity-0 group-hover/media:opacity-100 transition-opacity duration-700 animate-pulse"></div>
                  
                  <!-- Media Container -->
                  <div class="relative w-full min-h-[300px] bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-900 dark:to-black rounded-2xl overflow-hidden shadow-xl border-2 border-gray-200/60 dark:border-gray-700/50 group-hover/media:border-[#a50e34]/60 dark:group-hover/media:border-red-500/60 transition-all duration-500 flex items-center justify-center p-3">
                    <!-- Video -->
                    <div v-if="feature.type === 'video'" class="w-full bg-white dark:bg-gray-900 rounded-xl overflow-hidden flex items-center justify-center">
                      <video
                        :src="feature.content"
                        controls
                        class="w-full max-h-[400px] object-contain rounded-lg"
                      >
                        Ihr Browser unterstützt das Video-Element nicht.
                      </video>
                    </div>

                    <!-- Single Image -->
                    <div v-else-if="feature.type === 'image' && feature.content" class="w-full h-full min-h-[300px] relative cursor-pointer overflow-hidden flex items-center justify-center bg-gray-50 dark:bg-gray-900/50 rounded-xl" @click="openLightbox(feature.content)">
                      <img
                        :src="feature.content"
                        :alt="feature.title"
                        class="max-w-full max-h-full object-contain rounded-lg group-hover/media:scale-105 transition-transform duration-700"
                      />
                      <div class="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover/media:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                        <div class="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md px-3 py-1.5 rounded-full flex items-center gap-2 shadow-lg">
                          <MagnifyingGlassPlusIcon class="w-4 h-4 text-[#a50e34] dark:text-red-400" />
                          <span class="text-xs font-semibold text-gray-900 dark:text-white">Vergrößern</span>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Image Slider for Gallery -->
                    <div v-else-if="feature.type === 'gallery'" class="w-full min-h-[300px] relative bg-gray-50 dark:bg-gray-900 rounded-xl overflow-hidden">
                      <div class="w-full min-h-[300px] overflow-hidden relative flex items-center justify-center">
                        <template v-for="(img, i) in feature.images" :key="`${feature.id}-${i}`">
                          <div 
                            v-show="(galleryStates[feature.id] ?? 0) === i"
                            class="absolute inset-0 cursor-pointer flex items-center justify-center p-2"
                            @click="openLightbox(img)"
                          >
                            <img 
                              :src="img" 
                              :alt="feature.title"
                              class="max-w-full max-h-full w-auto h-auto min-h-[280px] object-contain rounded-lg"
                            />
                          </div>
                        </template>
                      </div>

                      <!-- Slider Dots -->
                      <div class="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                        <button 
                          v-for="(_, i) in feature.images" 
                          :key="i"
                          @click.stop="setGalleryIndex(feature.id, i)"
                          class="h-2 rounded-full transition-all duration-300"
                          :class="galleryStates[feature.id] === i ? 'bg-[#a50e34] w-6 shadow-lg shadow-[#a50e34]/50' : 'bg-gray-400/50 w-2 hover:bg-gray-400'"
                        ></button>
                      </div>

                      <!-- Slider Controls - Always visible on mobile -->
                      <button 
                        v-if="feature.images"
                        @click.stop="prevGalleryImage(feature.id, feature.images.length)"
                        class="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-md text-[#a50e34] dark:text-red-400 opacity-100 md:opacity-0 md:group-hover/media:opacity-100 transition-all duration-300 hover:bg-white dark:hover:bg-gray-700 hover:scale-110 shadow-lg"
                      >
                        <ChevronLeftIcon class="w-5 h-5" />
                      </button>
                      <button 
                        v-if="feature.images"
                        @click.stop="nextGalleryImage(feature.id, feature.images.length)"
                        class="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-md text-[#a50e34] dark:text-red-400 opacity-100 md:opacity-0 md:group-hover/media:opacity-100 transition-all duration-300 hover:bg-white dark:hover:bg-gray-700 hover:scale-110 shadow-lg"
                      >
                        <ChevronRightIcon class="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Hover Glow Effect -->
                <div class="absolute inset-0 rounded-3xl bg-gradient-to-r from-[#a50e34]/0 via-[#a50e34]/0 to-[#a50e34]/0 group-hover:from-[#a50e34]/5 group-hover:via-transparent group-hover:to-transparent transition-all duration-500 pointer-events-none"></div>
              </div>

              <!-- Desktop: Feature Content Card -->
              <div 
                class="hidden md:block relative"
                :class="index % 2 === 1 ? 'md:order-2' : ''"
              >
                <div class="relative h-full bg-gradient-to-br from-white via-white to-gray-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 backdrop-blur-md rounded-3xl p-10 shadow-2xl border-2 border-gray-200/60 dark:border-gray-700/50 group-hover:border-[#a50e34]/60 dark:group-hover:border-red-500/60 transition-all duration-500 overflow-hidden">
                  <!-- Decorative Background Element -->
                  <div class="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#a50e34]/5 to-transparent rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                  
                  <!-- Icon Badge -->
                  <div class="relative flex items-start justify-between mb-6">
                    <div class="flex items-center gap-4">
                      <div class="p-3 rounded-2xl bg-gradient-to-br from-[#a50e34]/10 to-red-600/10 dark:from-[#a50e34]/20 dark:to-red-600/20 border border-[#a50e34]/20 dark:border-red-500/20 group-hover:scale-110 transition-transform duration-300">
                        <component :is="feature.icon" class="w-7 h-7 text-[#a50e34] dark:text-red-400" />
                      </div>
                      <div class="px-4 py-1.5 rounded-full bg-gradient-to-r from-red-100 to-red-50 dark:from-red-900/40 dark:to-red-800/30 text-[#a50e34] dark:text-red-400 text-xs font-bold border border-[#a50e34]/20 dark:border-red-500/20">
                        #{{ String(index + 1).padStart(2, '0') }}
                      </div>
                    </div>
                  </div>

                  <!-- Title -->
                  <h3 class="relative text-4xl font-bold text-gray-900 dark:text-gray-100 mb-6 group-hover:text-[#a50e34] dark:group-hover:text-red-400 transition-colors duration-300 leading-tight">
                    {{ feature.title }}
                  </h3>

                  <!-- Description -->
                  <p class="relative text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-8">
                    {{ feature.description }}
                  </p>

                  <!-- Tags -->
                  <div v-if="feature.tags" class="relative flex flex-wrap gap-2.5">
                    <span 
                      v-for="tag in feature.tags" 
                      :key="tag" 
                      class="text-xs font-semibold px-4 py-1.5 bg-gray-100/80 dark:bg-gray-700/60 rounded-full text-gray-700 dark:text-gray-300 border border-gray-200/50 dark:border-gray-600/50 group-hover:border-[#a50e34]/30 dark:group-hover:border-red-500/30 transition-colors duration-300"
                    >
                      {{ tag }}
                    </span>
                  </div>

                  <!-- Hover Glow Effect -->
                  <div class="absolute inset-0 rounded-3xl bg-gradient-to-r from-[#a50e34]/0 via-[#a50e34]/0 to-[#a50e34]/0 group-hover:from-[#a50e34]/5 group-hover:via-transparent group-hover:to-transparent transition-all duration-500 pointer-events-none"></div>
                </div>
              </div>

              <!-- Desktop: Feature Media Container -->
              <div 
                class="hidden md:block relative"
                :class="index % 2 === 1 ? 'md:order-1' : ''"
              >
                <div class="relative w-full h-full min-h-[500px] flex items-center justify-center group/media">
                  <!-- Animated Background Glow -->
                  <div class="absolute -inset-6 bg-gradient-to-r from-[#a50e34]/20 via-red-600/20 to-[#a50e34]/20 rounded-[2.5rem] blur-2xl opacity-0 group-hover/media:opacity-100 transition-opacity duration-700 animate-pulse"></div>
                  
                  <!-- Media Container -->
                  <div class="relative w-full h-full bg-gradient-to-br from-white via-gray-50 to-gray-100 dark:from-gray-800 dark:via-gray-900 dark:to-black rounded-3xl overflow-hidden shadow-2xl border-2 border-gray-200/60 dark:border-gray-700/50 group-hover/media:border-[#a50e34]/60 dark:group-hover/media:border-red-500/60 transition-all duration-500 flex items-center justify-center p-6">
                    <!-- Video -->
                    <div v-if="feature.type === 'video'" class="w-full h-full bg-white dark:bg-gray-900 rounded-2xl overflow-hidden flex items-center justify-center">
                      <video
                        :src="feature.content"
                        controls
                        class="w-full h-full max-h-[500px] object-contain rounded-xl"
                      >
                        Ihr Browser unterstützt das Video-Element nicht.
                      </video>
                    </div>

                    <!-- Single Image -->
                    <div v-else-if="feature.type === 'image' && feature.content" class="w-full h-full relative cursor-pointer overflow-hidden flex items-center justify-center bg-gray-50 dark:bg-gray-900/50 rounded-2xl" @click="openLightbox(feature.content)">
                      <img
                        :src="feature.content"
                        :alt="feature.title"
                        class="max-w-full max-h-full object-contain rounded-xl group-hover/media:scale-105 transition-transform duration-700"
                      />
                      <div class="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover/media:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
                        <div class="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md px-4 py-2 rounded-full flex items-center gap-2 shadow-lg">
                          <MagnifyingGlassPlusIcon class="w-5 h-5 text-[#a50e34] dark:text-red-400" />
                          <span class="text-sm font-semibold text-gray-900 dark:text-white">Vergrößern</span>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Image Slider for Gallery -->
                    <div v-else-if="feature.type === 'gallery'" class="w-full h-full relative bg-gray-50 dark:bg-gray-900 rounded-2xl overflow-hidden">
                      <div class="h-full w-full overflow-hidden relative flex items-center justify-center">
                        <transition-group name="slide">
                          <div 
                            v-for="(img, i) in feature.images" 
                            v-show="galleryStates[feature.id] === i"
                            :key="img"
                            class="absolute inset-0 cursor-pointer flex items-center justify-center p-4"
                            @click="openLightbox(img)"
                          >
                            <img 
                              :src="img" 
                              :alt="feature.title"
                              class="max-w-full max-h-full object-contain rounded-xl"
                            />
                          </div>
                        </transition-group>
                      </div>

                      <!-- Slider Dots -->
                      <div class="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2.5 z-10">
                        <button 
                          v-for="(_, i) in feature.images" 
                          :key="i"
                          @click.stop="setGalleryIndex(feature.id, i)"
                          class="h-2 rounded-full transition-all duration-300"
                          :class="galleryStates[feature.id] === i ? 'bg-[#a50e34] w-8 shadow-lg shadow-[#a50e34]/50' : 'bg-gray-400/50 w-2 hover:bg-gray-400'"
                        ></button>
                      </div>

                      <!-- Slider Controls -->
                      <button 
                        v-if="feature.images"
                        @click.stop="prevGalleryImage(feature.id, feature.images.length)"
                        class="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-md text-[#a50e34] dark:text-red-400 opacity-0 group-hover/media:opacity-100 transition-all duration-300 hover:bg-white dark:hover:bg-gray-700 hover:scale-110 shadow-lg"
                      >
                        <ChevronLeftIcon class="w-6 h-6" />
                      </button>
                      <button 
                        v-if="feature.images"
                        @click.stop="nextGalleryImage(feature.id, feature.images.length)"
                        class="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-md text-[#a50e34] dark:text-red-400 opacity-0 group-hover/media:opacity-100 transition-all duration-300 hover:bg-white dark:hover:bg-gray-700 hover:scale-110 shadow-lg"
                      >
                        <ChevronRightIcon class="w-6 h-6" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Project Info Grid -->
    <section class="py-20 px-4 sm:px-6 lg:px-8">
      <div class="max-w-7xl mx-auto">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div class="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-8 rounded-2xl border-2 border-gray-300/60 dark:border-gray-600/50 shadow-lg text-center hover:shadow-xl transition-shadow duration-300 reveal">
            <h4 class="text-[#a50e34] font-bold mb-4 uppercase tracking-wider text-sm">Plattform</h4>
            <p class="text-gray-900 dark:text-white font-medium text-lg">WordPress + Elementor</p>
          </div>
          <div class="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-8 rounded-2xl border-2 border-gray-300/60 dark:border-gray-600/50 shadow-lg text-center hover:shadow-xl transition-shadow duration-300 reveal delay-100">
            <h4 class="text-[#a50e34] font-bold mb-4 uppercase tracking-wider text-sm">Entwicklungszeit</h4>
            <p class="text-gray-900 dark:text-white font-medium text-lg">Kontinuierliche Begleitung</p>
          </div>
          <div class="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-8 rounded-2xl border-2 border-gray-300/60 dark:border-gray-600/50 shadow-lg text-center hover:shadow-xl transition-shadow duration-300 reveal delay-200">
            <h4 class="text-[#a50e34] font-bold mb-4 uppercase tracking-wider text-sm">Kern-Fokus</h4>
            <p class="text-gray-900 dark:text-white font-medium text-lg">Custom Plugins & API Sync</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Back to Projekte -->
    <section class="py-20 px-4 sm:px-6 lg:px-8">
      <div class="max-w-7xl mx-auto text-center reveal">
        <router-link
          to="/portfolio"
          class="inline-flex items-center space-x-3 px-10 py-4 bg-white dark:bg-gray-800 text-[#a50e34] dark:text-red-400 font-bold rounded-2xl shadow-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300 hover:scale-105 border border-[#a50e34]/20"
        >
          <ArrowLeftIcon class="w-5 h-5" />
          <span>Zurück zur Übersicht</span>
        </router-link>
      </div>
    </section>

    <!-- Lightbox Modal -->
    <Transition name="fade">
      <div v-if="lightboxUrl" class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm" @click="closeLightbox">
        <button class="absolute top-6 right-6 text-white hover:text-[#a50e34] transition-colors p-2" @click="closeLightbox">
          <XMarkIcon class="w-8 h-8" />
        </button>
        <img :src="lightboxUrl" alt="Lightbox Image" class="max-w-full max-h-full object-contain rounded-lg shadow-2xl" @click.stop />
      </div>
    </Transition>

    <StandardFooter accent-color="#a50e34" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import Navigation from '../../components/Navigation.vue'
import StandardFooter from '../../components/StandardFooter.vue'
import { useTheme } from '../../composables/useTheme'
import { getPortfolioProjectByPageUrl } from '../../firebase/portfolio'
import { logger } from '../../utils/logger'
import { 
  ArrowLeftIcon, 
  ChevronLeftIcon, 
  ChevronRightIcon, 
  MagnifyingGlassPlusIcon,
  XMarkIcon,
  GlobeAltIcon,
  VideoCameraIcon,
  CpuChipIcon,
  CalendarIcon,
  UsersIcon,
  FilmIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  Squares2X2Icon,
  AcademicCapIcon,
  CommandLineIcon
} from '@heroicons/vue/24/outline'

const { isDark } = useTheme()

// Logo aus Firebase laden
const logoUrl = ref<string>('')
const loadProjectData = async () => {
  try {
    const project = await getPortfolioProjectByPageUrl('/projects/m-drei')
    if (project) {
      // Verwende Logo aus Firebase, wenn vorhanden
      if (isDark.value && project.logoDark) {
        logoUrl.value = project.logoDark
      } else if (!isDark.value && project.logoLight) {
        logoUrl.value = project.logoLight
      } else {
        // Fallback auf hardcodierte Logos
        logoUrl.value = isDark.value ? '/projects/m-drei/LogoWhite.webp' : '/projects/m-drei/LogoSchwarz.webp'
      }
      
      // Accent Color laden und CSS-Variable setzen
      if (project.accentColor) {
        if (typeof document !== 'undefined') {
          document.documentElement.style.setProperty('--project-accent', project.accentColor)
        }
      } else {
        // Fallback: Setze Standard-Farbe für M-Drei
        if (typeof document !== 'undefined') {
          document.documentElement.style.setProperty('--project-accent', '#a50e34')
        }
      }
    } else {
      // Fallback auf hardcodierte Logos, wenn kein Firebase-Projekt gefunden
      logoUrl.value = isDark.value ? '/projects/m-drei/LogoWhite.webp' : '/projects/m-drei/LogoSchwarz.webp'
      // Fallback: Setze Standard-Farbe für M-Drei
      if (typeof document !== 'undefined') {
        document.documentElement.style.setProperty('--project-accent', '#a50e34')
      }
    }
  } catch (error) {
    logger.error('Fehler beim Laden der Projekt-Daten', error)
    // Fallback auf hardcodierte Logos bei Fehler
    logoUrl.value = isDark.value ? '/projects/m-drei/LogoWhite.webp' : '/projects/m-drei/LogoSchwarz.webp'
    // Fallback: Setze Standard-Farbe für M-Drei
    if (typeof document !== 'undefined') {
      document.documentElement.style.setProperty('--project-accent', '#a50e34')
    }
  }
}

// Logo aktualisieren, wenn Theme wechselt
const updateLogo = async () => {
  try {
    const project = await getPortfolioProjectByPageUrl('/projects/m-drei')
    if (project) {
      if (isDark.value && project.logoDark) {
        logoUrl.value = project.logoDark
      } else if (!isDark.value && project.logoLight) {
        logoUrl.value = project.logoLight
      } else {
        // Fallback auf hardcodierte Logos
        logoUrl.value = isDark.value ? '/projects/m-drei/LogoWhite.webp' : '/projects/m-drei/LogoSchwarz.webp'
      }
    } else {
      logoUrl.value = isDark.value ? '/projects/m-drei/LogoWhite.webp' : '/projects/m-drei/LogoSchwarz.webp'
    }
  } catch (error) {
    logoUrl.value = isDark.value ? '/projects/m-drei/LogoWhite.webp' : '/projects/m-drei/LogoSchwarz.webp'
  }
}

// Watch für Theme-Änderungen - außerhalb von onMounted für sofortige Reaktion
const stopWatch = watch(isDark, () => {
  updateLogo()
}, { immediate: false })

const showAllTechs = ref(false)
const isMobile = ref(false)

const checkIfMobile = () => {
  if (typeof window === 'undefined') return false
  return window.innerWidth < 768
}

const displayedTechnologies = computed(() => {
  // Auf Desktop immer alle Tags anzeigen
  if (!isMobile.value) {
    return project.value.technologies
  }
  // Auf mobil: Wenn "Mehr anzeigen" geklickt wurde, alle anzeigen, sonst nur erste 8
  if (showAllTechs.value) {
    return project.value.technologies
  }
  return project.value.technologies.slice(0, 8)
})

const stats = [
  { icon: CpuChipIcon, value: '5+', label: 'Custom Plugins' },
  { icon: VideoCameraIcon, value: 'Vimeo', label: 'API Integration' },
  { icon: CalendarIcon, value: 'Zoom', label: 'Event Sync' },
  { icon: UsersIcon, value: 'RapidMail', label: 'Newsletter' }
]

const project = ref({
  technologies: ['WordPress', 'Elementor', 'Vimeo API', 'Zoom API', 'RapidMail', 'Custom Plugins', 'UX', 'Search Engine', 'CRM Integration', 'Email Marketing', 'Custom Widget', 'Configuration', 'Event Management', 'Admin Dashboard', 'Playwright', 'Automation Testing', 'CI/CD', 'Monitoring'],
  features: [
    {
      id: 'feature-fronten',
      title: 'Fronten-Auswahl Vorschau',
      description: 'Um Kunden die Auswahl der richtigen Möbelfronten zu erleichtern, wurde ein interaktiver Vorschau-Bereich integriert, der verschiedene Varianten anschaulich präsentiert.',
      type: 'video',
      content: '/projects/m-drei/AuswahlFronten.mp4',
      tags: ['Frontend', 'Elementor', 'UX'],
      icon: FilmIcon,
      order: 1
    },
    {
      id: 'feature-vimeo-search',
      title: 'Tutorial-Suche über Transkripte',
      description: 'M-Drei hostet zahlreiche Tutorials für die "Meisterplaner" Software bei Vimeo. Ich habe ein Plugin entwickelt, das diese Videos indexiert, die Transkripte speichert und sie über eine Suchfunktion durchsuchbar macht. So finden Kunden in Sekunden genau die Stelle im Video, die ihre Frage beantwortet.',
      type: 'gallery',
      images: [
        '/projects/m-drei/Video Tutorials über Transkripte durchsuchen.webp',
        '/projects/m-drei/IndexedVideos.webp'
      ],
      tags: ['Vimeo API', 'Search Engine', 'Automation'],
      icon: MagnifyingGlassIcon,
      order: 2
    },
    {
      id: 'feature-licenses',
      title: 'Lizenzanfragen-Management',
      description: 'Ein maßgeschneidertes Plugin zur Verwaltung von Software-Lizenzanfragen. Es beinhaltet konfigurierbare E-Mail-Templates und eine Anbindung an Rapidmail, wodurch Kunden bei Interesse automatisch dem Newsletter hinzugefügt werden können.',
      type: 'image',
      content: '/projects/m-drei/Lizenzformular für den Meisterplaner.webp',
      tags: ['CRM Integration', 'Email Marketing', 'RapidMail'],
      icon: DocumentTextIcon,
      order: 3
    },
    {
      id: 'feature-playlist',
      title: 'Advanced Vimeo Video Playlist',
      description: 'Ein speziell entwickeltes Elementor-Widget, das als konfigurierbares Vimeo-Showcase fungiert. Es passt sich nahtlos an das Design an und bietet umfangreiche Einstellungsmöglichkeiten für die Video-Präsentation.',
      type: 'image',
      content: '/projects/m-drei/Plugin-VimeoVideoPlaylist.webp',
      tags: ['Custom Widget', 'Video UX', 'Configuration'],
      icon: Squares2X2Icon,
      order: 4
    },
    {
      id: 'feature-events',
      title: 'Event- & Schulungssystem',
      description: 'Für die Vor-Ort und Online-Schulungen wurde ein System entwickelt, das automatisch Zoom nach Webinaren abfragt und diese auf der Webseite bereitstellt. Inklusive Teilnehmerverwaltung, Anmeldeformularen und einer einfach zu bedienenden Administration im WordPress-Backend.',
      type: 'gallery',
      images: [
        '/projects/m-drei/Webinare und Vor Ort Schulunge - Anmeldungen und Vimeeo.webp',
        '/projects/m-drei/WebinarBackend - Eventerstellung.webp'
      ],
      tags: ['Zoom API', 'Event Management', 'Admin Dashboard'],
      icon: AcademicCapIcon,
      order: 5
    },
    {
      id: 'feature-playwright-tests',
      title: 'Automation Testsuite mit Playwright',
      description: 'Um die Qualität und Verfügbarkeit kritischer Funktionen sicherzustellen, habe ich eine umfassende Automation Testsuite mit Playwright entwickelt. Diese prüft kontinuierlich das Lizenzformular sowie das Laden von Lieferzeiten und erstellt detaillierte Reports. Bei Problemen werden sowohl die Firma als auch ich sofort informiert, sodass ich unverzüglich reagieren kann.',
      type: 'image',
      content: '/projects/m-drei/Playwright Tests.webp',
      tags: ['Playwright', 'Automation Testing', 'CI/CD', 'Monitoring'],
      icon: CommandLineIcon,
      order: 6
    }
  ]
})

// Gallery Slider Logic
const galleryStates = ref<Record<string, number>>({})
const galleryIntervals: Record<string, any> = {}

const initGalleries = () => {
  project.value.features.forEach(f => {
    if (f.type === 'gallery') {
      galleryStates.value[f.id] = 0
      startGalleryTimer(f.id, f.images!.length)
    }
  })
}

const startGalleryTimer = (id: string, length: number) => {
  stopGalleryTimer(id)
  galleryIntervals[id] = setInterval(() => {
    nextGalleryImage(id, length)
  }, 5000)
}

const stopGalleryTimer = (id: string) => {
  if (galleryIntervals[id]) {
    clearInterval(galleryIntervals[id])
  }
}

const nextGalleryImage = (id: string, length: number) => {
  galleryStates.value[id] = (galleryStates.value[id] + 1) % length
  startGalleryTimer(id, length)
}

const prevGalleryImage = (id: string, length: number) => {
  galleryStates.value[id] = (galleryStates.value[id] - 1 + length) % length
  startGalleryTimer(id, length)
}

const setGalleryIndex = (id: string, index: number) => {
  galleryStates.value[id] = index
  const f = project.value.features.find(feat => feat.id === id)
  if (f && f.images) {
    startGalleryTimer(id, f.images.length)
  }
}

// Lightbox Logic
const lightboxUrl = ref<string | null>(null)
const openLightbox = (url: string) => {
  lightboxUrl.value = url
  document.body.style.overflow = 'hidden'
}
const closeLightbox = () => {
  lightboxUrl.value = null
  document.body.style.overflow = ''
}

// Scroll Animation Logic
const initScrollReveal = () => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('active')
      }
    })
  }, { threshold: 0.1 })

  document.querySelectorAll('.reveal').forEach(el => observer.observe(el))
}

const sortedFeatures = computed(() => {
  return [...project.value.features].sort((a, b) => a.order - b.order)
})

const handleResize = () => {
  isMobile.value = checkIfMobile()
  // Auf Desktop immer alle Tags anzeigen
  if (!isMobile.value) {
    showAllTechs.value = true
  }
}

onMounted(async () => {
  // Stelle sicher, dass die Seite oben startet
  window.scrollTo({ top: 0, behavior: 'instant' })
  
  // Lade Projekt-Daten und Logo
  await loadProjectData()
  
  // Prüfe ob mobil
  isMobile.value = checkIfMobile()
  if (!isMobile.value) {
    showAllTechs.value = true
  }
  
  // Listener für Resize hinzufügen
  window.addEventListener('resize', handleResize)
  
  initGalleries()
  initScrollReveal()
  // Füge Klasse zu html hinzu für Scrollbar-Styling
  document.documentElement.classList.add('m-drei-page')
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  Object.values(galleryIntervals).forEach(clearInterval)
  // Stoppe Watch für Theme-Änderungen
  stopWatch()
  // Entferne Klasse von html
  document.documentElement.classList.remove('m-drei-page')
  // Entferne CSS-Variable
  if (typeof document !== 'undefined') {
    document.documentElement.style.removeProperty('--project-accent')
  }
})
</script>

<style scoped>
@keyframes blob {
  0% { transform: translate(0px, 0px) scale(1); }
  33% { transform: translate(30px, -50px) scale(1.1); }
  66% { transform: translate(-20px, 20px) scale(0.9); }
  100% { transform: translate(0px, 0px) scale(1); }
}

.animate-blob {
  animation: blob 7s infinite;
}

.animation-delay-2000 {
  animation-delay: 2s;
}

/* Scroll Reveal */
.reveal {
  opacity: 0;
  transform: translateY(30px);
  transition: all 1s cubic-bezier(0.4, 0, 0.2, 1);
}

.reveal.active {
  opacity: 1;
  transform: translateY(0);
}

.delay-100 { transition-delay: 0.1s; }
.delay-200 { transition-delay: 0.2s; }
.delay-300 { transition-delay: 0.3s; }
.delay-400 { transition-delay: 0.4s; }
.delay-500 { transition-delay: 0.5s; }

/* Slide Animation */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.8s ease;
}

.slide-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.slide-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

/* Fade Animation (Lightbox) */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

<style>
/* Mauszeiger und Scrollwheel in Featurefarbe für M-Drei Seite */
/* Überschreibt MouseEffect-Komponente Styles */
:deep(.outer-ring) {
  border-color: rgba(165, 14, 52, 0.3) !important;
}

:deep(.outer-ring.hover-active) {
  border-color: rgba(165, 14, 52, 0.4) !important;
}

:deep(.custom-cursor) {
  background: rgba(165, 14, 52, 0.6) !important;
  box-shadow: 
    0 0 10px rgba(165, 14, 52, 0.5),
    0 0 20px rgba(165, 14, 52, 0.3),
    inset 0 0 10px rgba(165, 14, 52, 0.2) !important;
}

:deep(.custom-cursor.hover-active) {
  background: rgba(165, 14, 52, 0.7) !important;
  box-shadow: 
    0 0 15px rgba(165, 14, 52, 0.6),
    0 0 30px rgba(165, 14, 52, 0.4),
    0 0 45px rgba(165, 14, 52, 0.2),
    inset 0 0 15px rgba(165, 14, 52, 0.3) !important;
}

:deep(.trail-dot) {
  background: rgba(165, 14, 52, 0.5) !important;
}
</style>

<style>
/* Scrollwheel in Featurefarbe für M-Drei Seite - Mit höherer Spezifität */
html.m-drei-page,
html.m-drei-page * {
  scrollbar-width: thin;
  scrollbar-color: rgba(165, 14, 52, 0.6) rgba(0, 0, 0, 0.05) !important;
}

html.m-drei-page ::-webkit-scrollbar,
html.m-drei-page body ::-webkit-scrollbar {
  width: 12px !important;
  height: 12px !important;
}

html.m-drei-page ::-webkit-scrollbar-track,
html.m-drei-page body ::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05) !important;
  border-left: none !important;
  border-radius: 0 !important;
}

html.m-drei-page ::-webkit-scrollbar-thumb,
html.m-drei-page body ::-webkit-scrollbar-thumb {
  background: rgba(165, 14, 52, 0.6) !important;
  background-image: none !important;
  border-radius: 6px !important;
  border: 2px solid transparent !important;
  background-clip: padding-box !important;
  box-shadow: none !important;
  animation: none !important;
  transform: none !important;
}

html.m-drei-page ::-webkit-scrollbar-thumb:hover,
html.m-drei-page body ::-webkit-scrollbar-thumb:hover {
  background: rgba(165, 14, 52, 0.8) !important;
  background-image: none !important;
  background-clip: padding-box !important;
  box-shadow: none !important;
  animation: none !important;
  transform: none !important;
}

html.m-drei-page ::-webkit-scrollbar-thumb:active,
html.m-drei-page body ::-webkit-scrollbar-thumb:active {
  background: rgba(165, 14, 52, 0.9) !important;
  background-image: none !important;
  box-shadow: none !important;
  transform: none !important;
}

/* Dark Mode Scrollwheel für M-Drei Seite */
html.m-drei-page.dark,
html.m-drei-page.dark * {
  scrollbar-color: rgba(165, 14, 52, 0.7) rgba(255, 255, 255, 0.05) !important;
}

html.m-drei-page.dark ::-webkit-scrollbar-track,
html.m-drei-page.dark body ::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05) !important;
  border-left: none !important;
}

html.m-drei-page.dark ::-webkit-scrollbar-thumb,
html.m-drei-page.dark body ::-webkit-scrollbar-thumb {
  background: rgba(165, 14, 52, 0.7) !important;
  background-image: none !important;
  box-shadow: none !important;
  animation: none !important;
}

html.m-drei-page.dark ::-webkit-scrollbar-thumb:hover,
html.m-drei-page.dark body ::-webkit-scrollbar-thumb:hover {
  background: rgba(165, 14, 52, 0.9) !important;
  background-image: none !important;
  box-shadow: none !important;
  animation: none !important;
  transform: none !important;
}

html.m-drei-page.dark ::-webkit-scrollbar-thumb:active,
html.m-drei-page.dark body ::-webkit-scrollbar-thumb:active {
  background: rgba(165, 14, 52, 1) !important;
  background-image: none !important;
  box-shadow: none !important;
  transform: none !important;
}

/* IT Text Unterstreichung in M-Drei Farbe */
html.m-drei-page .it-text::after {
  background-color: rgb(165, 14, 52) !important; /* #a50e34 */
}

html.m-drei-page.dark .it-text::after {
  background-color: rgb(248, 113, 113) !important; /* red-400 */
}
</style>
o